"use client";

import { useCallback, useEffect, useState } from "react";
import { useOrganization } from "@/hooks/useOrganization";
import type { OrgRecord } from "@/hooks/useOrganization";
import { authClient } from "@/lib/auth";
import { organizationsApi } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";

interface OrgMember {
  id: string;
  userId: string;
  role: string;
  createdAt: string;
  user?: { name: string; email: string; image?: string | null };
}

interface OrgInvitation {
  id: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string;
}

export function OrgSettings() {
  const {
    activeOrganization,
    activeOrganizationId,
    isLoading,
    isAdmin,
    isViewOnly,
    getChildren,
    createChild,
    setActiveOrganization,
    inviteMember,
    cancelInvitation,
  } = useOrganization();
  const [childOrgs, setChildOrgs] = useState<OrgRecord[]>([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [newOrgSlug, setNewOrgSlug] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editable org fields
  const orgData = activeOrganization as Record<string, unknown> | null;
  const [orgName, setOrgName] = useState((orgData?.name as string) || "");
  const [orgSlug, setOrgSlug] = useState((orgData?.slug as string) || "");
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Members & invitations (admin only)
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [invitations, setInvitations] = useState<OrgInvitation[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "member">("member");
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);

  useEffect(() => {
    if (orgData) {
      setOrgName((orgData.name as string) || "");
      setOrgSlug((orgData.slug as string) || "");
    }
  }, [orgData]);

  const loadChildren = useCallback(async () => {
    if (!activeOrganizationId) return;
    setLoadingChildren(true);
    try {
      const children = await getChildren();
      setChildOrgs(children);
    } catch {
      // Silently fail — children may not exist
    } finally {
      setLoadingChildren(false);
    }
  }, [activeOrganizationId, getChildren]);

  useEffect(() => {
    loadChildren();
  }, [loadChildren]);

  const loadMembers = useCallback(async () => {
    if (!activeOrganizationId || !isAdmin) return;
    setLoadingMembers(true);
    try {
      const fullOrg = await authClient.organization.getFullOrganization({
        query: { organizationId: activeOrganizationId },
      });
      if (fullOrg.data) {
        const data = fullOrg.data as Record<string, unknown>;
        setMembers((data.members as OrgMember[]) || []);
        setInvitations((data.invitations as OrgInvitation[]) || []);
      }
    } catch {
      // Silently fail
    } finally {
      setLoadingMembers(false);
    }
  }, [activeOrganizationId, isAdmin]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleUpdateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeOrganizationId) return;
    setError(null);
    setSaveSuccess(false);
    setSaving(true);

    try {
      const result = await authClient.organization.update({
        organizationId: activeOrganizationId,
        data: { name: orgName.trim(), slug: orgSlug.trim() || undefined },
      });
      if (result.error) {
        throw new Error(result.error.message || "Failed to update organization");
      }
      setSaveSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateChild = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setCreating(true);

    try {
      await createChild({
        name: newOrgName.trim(),
        slug: newOrgSlug.trim() || undefined,
      });
      setNewOrgName("");
      setNewOrgSlug("");
      setShowCreateModal(false);
      await loadChildren();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create child org");
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError(null);
    setInviteSuccess(false);
    setInviting(true);

    try {
      await inviteMember(inviteEmail.trim(), inviteRole);
      setInviteEmail("");
      setInviteRole("member");
      setInviteSuccess(true);
      await loadMembers();
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : "Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await cancelInvitation(invitationId);
      await loadMembers();
    } catch {
      // Silently fail
    }
  };

  // Show loading skeleton while BetterAuth hooks are fetching
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="h-6 w-48 animate-pulse rounded bg-slate-200" />
          <div className="mt-4 space-y-3">
            <div className="h-10 w-full animate-pulse rounded-xl bg-slate-100" />
            <div className="h-10 w-full animate-pulse rounded-xl bg-slate-100" />
          </div>
        </div>
      </div>
    );
  }

  if (!activeOrganization) {
    return <CreateOrgPrompt />;
  }

  const role = (orgData?.activeMemberRole as string) || "member";

  return (
    <div className="space-y-6">
      {/* Org details */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Organization Details</h2>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium capitalize text-slate-600">
            {role}
          </span>
        </div>

        <form onSubmit={handleUpdateOrg} className="space-y-4">
          <div>
            <label
              htmlFor="org-name"
              className="mb-1 block text-sm font-medium text-slate-700"
            >
              Name
            </label>
            <input
              id="org-name"
              type="text"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
              required
            />
          </div>

          <div>
            <label
              htmlFor="org-slug"
              className="mb-1 block text-sm font-medium text-slate-700"
            >
              Slug
            </label>
            <input
              id="org-slug"
              type="text"
              value={orgSlug}
              onChange={(e) => setOrgSlug(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
          {saveSuccess && <p className="text-sm text-green-600">Organization updated</p>}

          <button
            type="submit"
            disabled={saving}
            className="rounded-xl bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save changes"}
          </button>
        </form>
      </div>

      {/* Invite Members (admin only) */}
      {isAdmin && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="mb-4 text-lg font-semibold text-slate-900">
            Invite Team Members
          </h2>

          <form onSubmit={handleInvite} className="flex flex-wrap items-end gap-3">
            <div className="min-w-0 flex-1">
              <label
                htmlFor="invite-email"
                className="mb-1 block text-sm font-medium text-slate-700"
              >
                Email
              </label>
              <input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
                placeholder="user@example.com"
                required
              />
            </div>
            <div className="w-32">
              <label
                htmlFor="invite-role"
                className="mb-1 block text-sm font-medium text-slate-700"
              >
                Role
              </label>
              <select
                id="invite-role"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as "admin" | "member")}
                className="w-full rounded-xl border border-slate-200 px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={inviting}
              className="rounded-xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
            >
              {inviting ? "Sending..." : "Invite"}
            </button>
          </form>

          {inviteError && <p className="mt-2 text-sm text-red-600">{inviteError}</p>}
          {inviteSuccess && (
            <p className="mt-2 text-sm text-green-600">Invitation sent</p>
          )}

          {/* Members list */}
          <div className="mt-6">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">Members</h3>
            {loadingMembers ? (
              <p className="text-sm text-slate-400">Loading...</p>
            ) : members.length === 0 ? (
              <p className="text-sm text-slate-500">No members yet.</p>
            ) : (
              <div className="space-y-2">
                {members.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {member.user?.name || "—"}
                      </p>
                      <p className="text-xs text-slate-400">
                        {member.user?.email || "—"}
                      </p>
                    </div>
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium capitalize text-slate-600">
                      {member.role}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pending invitations */}
          {invitations.filter((inv) => inv.status === "pending").length > 0 && (
            <div className="mt-6">
              <h3 className="mb-3 text-sm font-semibold text-slate-700">
                Pending Invitations
              </h3>
              <div className="space-y-2">
                {invitations
                  .filter((inv) => inv.status === "pending")
                  .map((inv) => (
                    <div
                      key={inv.id}
                      className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3"
                    >
                      <div>
                        <p className="text-sm font-medium text-slate-900">{inv.email}</p>
                        <p className="text-xs text-slate-400">
                          {inv.role === "admin" ? "Admin" : "Member"} &middot; Expires{" "}
                          {new Date(inv.expiresAt).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => handleCancelInvitation(inv.id)}
                        className="text-xs text-slate-400 transition hover:text-red-600"
                      >
                        Cancel
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Child orgs */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">
            Affiliated Organizations
          </h2>
          {isAdmin && !isViewOnly && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              Create child
            </button>
          )}
        </div>

        {loadingChildren ? (
          <p className="text-sm text-slate-400">Loading...</p>
        ) : childOrgs.length === 0 ? (
          <p className="text-sm text-slate-500">No child organizations yet.</p>
        ) : (
          <div className="space-y-2">
            {childOrgs.map((child) => (
              <div
                key={child.id}
                className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3"
              >
                <div>
                  <p className="text-sm font-medium text-slate-900">{child.name}</p>
                  {child.slug && <p className="text-xs text-slate-400">{child.slug}</p>}
                </div>
                <button
                  onClick={() => setActiveOrganization(child.id)}
                  className="text-xs font-medium text-slate-500 transition hover:text-slate-700"
                >
                  Switch
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Create child modal (inline) */}
        {showCreateModal && (
          <form
            onSubmit={handleCreateChild}
            className="mt-4 space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4"
          >
            <div>
              <label
                htmlFor="child-name"
                className="mb-1 block text-xs font-medium text-slate-700"
              >
                Name
              </label>
              <input
                id="child-name"
                type="text"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-400"
                required
              />
            </div>
            <div>
              <label
                htmlFor="child-slug"
                className="mb-1 block text-xs font-medium text-slate-700"
              >
                Slug (optional)
              </label>
              <input
                id="child-slug"
                type="text"
                value={newOrgSlug}
                onChange={(e) => setNewOrgSlug(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-400"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={creating}
                className="rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
              >
                {creating ? "Creating..." : "Create"}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="rounded-lg px-4 py-2 text-xs font-medium text-slate-500 transition hover:bg-slate-100"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function CreateOrgPrompt() {
  const { setActiveOrganization } = useAuth();
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setError(null);
    setCreating(true);

    try {
      const org = await organizationsApi.createPersonal(name.trim());
      await setActiveOrganization(org.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create organization");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <h2 className="mb-2 text-lg font-semibold text-slate-900">
        Create your organization
      </h2>
      <p className="mb-6 text-sm text-slate-500">
        Set up an organization to manage your team, invite members, and configure
        settings.
      </p>

      <form onSubmit={handleCreate} className="space-y-4">
        <div>
          <label
            htmlFor="create-org-name"
            className="mb-1 block text-sm font-medium text-slate-700"
          >
            Organization name
          </label>
          <input
            id="create-org-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
            placeholder="Your company or team name"
            required
          />
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <button
          type="submit"
          disabled={creating}
          className="rounded-xl bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
        >
          {creating ? "Creating..." : "Create organization"}
        </button>
      </form>
    </div>
  );
}
